import { ComboboxButton } from '@headlessui/react';
import React, { useRef, useState } from 'react';
import { Control, FieldPathByValue, FieldValues } from 'react-hook-form';

import { ChevronDownIcon, SearchIcon } from '../../assets/icons/index';
import { companyIndustryTranslation } from '../../pages/companies/view-company-modal/view-company-modal.types';
import { CompanyIndustry } from '../../types/company.types';
import {
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../combobox/index';

export type CompanyIndustryItemsListProps<
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, unknown>,
> = {
  control: Control<TFieldValues>;
  items: [CompanyIndustry, string][];
  name: TPath;
};

const CompanyIndustryItemsList = <
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, unknown>,
>({
  control,
  items,
  name,
}: CompanyIndustryItemsListProps<TFieldValues, TPath>) =>
  items.map(([companyIndustry, companyIndustryTitle]) => (
    <FormComboboxOption
      control={control}
      key={companyIndustry}
      name={name}
      value={companyIndustry as TFieldValues[TPath]}
    >
      <span className="text-sm font-normal text-gray-700">{companyIndustryTitle}</span>
    </FormComboboxOption>
  ));

export type CompanyIndustryComboboxProps<
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, unknown>,
> = {
  control: Control<TFieldValues>;
  name: TPath;
};

export const CompanyIndustryCombobox = <
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, unknown>,
>({
  control,
  name,
}: CompanyIndustryComboboxProps<TFieldValues, TPath>) => {
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLInputElement>(null);

  const filteredList = Object.entries(companyIndustryTranslation)
    .filter(([_, translatedKey]) => {
      return translatedKey.toLowerCase().includes(search.toLowerCase().trim());
    })
    .sort((a, b) => {
      const aValue = a[0];
      const bValue = b[0];

      const aLabel = a[1];
      const bLabel = b[1];

      if (aValue === CompanyIndustry.OTHER) return 1;
      if (bValue === CompanyIndustry.OTHER) return -1;

      return aLabel.localeCompare(bLabel);
    });

  return (
    <FormCombobox className="relative" control={control} name={name}>
      <div className="relative">
        <FormComboboxInput
          className={'absolute z-0 bg-gray-900'}
          control={control}
          customValue={(value) =>
            value ? companyIndustryTranslation[value as CompanyIndustry] : value
          }
          icon={<ChevronDownIcon className="mt-3" />}
          name={name}
          placeholder="Industry"
          readOnly
        />
        <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" />
      </div>
      <ComboboxOptions className="w-full">
        <div className="relative flex items-center" onClick={() => ref.current?.focus()}>
          <SearchIcon className="absolute ml-[6px]" />
          <input
            className="w-full rounded border-[2px] border-transparent bg-gray-50 p-[6px] pl-8 text-gray-700 outline-none placeholder:text-gray-400 focus:border-brand-700 focus:bg-white"
            onChange={({ target: { value } }) => setSearch(value)}
            ref={ref}
            value={search}
          />
        </div>
        <CompanyIndustryItemsList control={control} items={filteredList} name={name} />
      </ComboboxOptions>
    </FormCombobox>
  );
};
